/**
 * Moving bouts while the event runs, and the judge page following the moves.
 * Pure functions; utils/running-order.test.js checks them.
 *
 * The running order is events/{id}.boutOrder (pairKeys) laid over the roster
 * by applyBoutOrder, and a bout's number is its place in it. A fighter in the
 * toilet or running late means the next bout goes first: the admin moves it
 * on the live results dashboard (pages/event-results.jsx), and the three
 * judge pages, which read the order with judgeGetEvent, follow it. Judges
 * never reorder on their own screens, so all three always show one order.
 *
 * Moving live, a bout the judges have started (any judge has locked a round
 * or submitted: in progress, scored) keeps its place and so its number, on
 * the judge pages and in the Partner API. The others (scheduled, cancelled)
 * move around it. So does a pair the dashboard can't show (a corner missing):
 * it holds its slot, so no number moves under the judges.
 */

import { getUniqueMatchedFighters } from "./common";
import { applyBoutOrder } from "./event-fighter-filters";

/** Statuses (utils/event-results.js) that hold a bout in its place. */
export const HELD_STATUSES = Object.freeze(["progress", "scored", "official"]);

export const isHeld = (m) => !!m && HELD_STATUSES.includes(m.status);

/**
 * Every pair in running order, the dashboard's hidden ones (a corner
 * missing) included: what a new boutOrder is written from, so their numbers
 * hold. The same pairs and order as buildBouts and functions/judging.js.
 */
export function runningOrderKeys(eventFighters, event) {
    const pairs = getUniqueMatchedFighters(eventFighters || []);
    return applyBoutOrder(
        Object.keys(pairs).map((pairKey) => ({ pairKey })),
        event?.boutOrder
    ).map((e) => e.pairKey);
}

/**
 * The running order as slots: each pair, and whether it holds its place.
 * @param {string[]} keys runningOrderKeys
 * @param {Array<{key, status}>} matches the dashboard's matches
 * @returns {Array<{key: string, held: boolean}>}
 */
export function liveSlots(keys, matches) {
    const byKey = new Map((matches || []).map((m) => [m.key, m]));
    return (keys || []).map((key) => {
        const m = byKey.get(key);
        return { key, held: !m || isHeld(m) };
    });
}

/**
 * One bout moved among the slots that aren't held; held ones stay put.
 *
 * @param {Array<{key, held}>} slots liveSlots
 * @param {string} key the bout to move
 * @param {"up"|"down"|{before: string}} move up and down step past the next
 *   movable bout (the arrows); {before} drops it in front of that movable
 *   bout (a drag)
 * @returns {string[]|null} the new running order, or null when nothing moves
 */
export function moveLive(slots, key, move) {
    const free = slots.filter((s) => !s.held).map((s) => s.key);
    const from = free.indexOf(key);
    if (from < 0) return null;
    const rest = free.filter((k) => k !== key);
    let at;
    if (move === "up") {
        at = from - 1;
    } else if (move === "down") {
        at = from + 1;
    } else if (move && typeof move.before === "string") {
        at = rest.indexOf(move.before);
    } else {
        return null;
    }
    if (at < 0 || at > rest.length) return null;
    rest.splice(at, 0, key);
    let i = 0;
    const out = slots.map((s) => (s.held ? s.key : rest[i++]));
    return out.some((k, j) => k !== slots[j].key) ? out : null;
}

/**
 * The arrows the reorder list offers for one bout, each as the running order
 * it would write, or null where that arrow does nothing.
 * @returns {{up: string[]|null, down: string[]|null}}
 */
export function liveMoves(slots, key) {
    return { up: moveLive(slots, key, "up"), down: moveLive(slots, key, "down") };
}

/** A bout's number in a running order (every pair counts, as on the card). */
export const numberIn = (order, key) => order.indexOf(key) + 1;

/* ── The judge page ─────────────────────────────────────────────────────
 * Lists here are the judge page's matches ({boutKey, n}, running order) and
 * cards its own cards by boutKey. */

/**
 * The bouts on both lists in a different order from each other. A bout
 * added or taken off renumbers the ones after it, but that isn't a reorder.
 */
export function isReordered(prev, next) {
    const inNext = new Set((next || []).map((m) => m.boutKey));
    const a = (prev || []).map((m) => m.boutKey).filter((k) => inNext.has(k));
    const inPrev = new Set(a);
    const b = (next || []).map((m) => m.boutKey).filter((k) => inPrev.has(k));
    return a.some((k, i) => k !== b[i]);
}

/**
 * The match a judge scores next: the first after their furthest submitted
 * card that they haven't submitted, else the first they haven't submitted.
 */
export function nextToScore(matches, cards) {
    const list = matches || [];
    const done = (m) => !!cards?.[m.boutKey]?.submitted;
    let last = -1;
    list.forEach((m, i) => {
        if (done(m)) last = i;
    });
    const m = list.slice(last + 1).find((x) => !done(x)) ?? list.find((x) => !done(x));
    return m?.boutKey ?? null;
}

/** Nothing scored, locked or submitted on it yet (no card counts too). */
export function isUntouched(card) {
    if (!card) return true;
    if (card.submitted || card.reopenedFrom) return false;
    if ((card.rounds || []).some(Boolean)) return false;
    return !(card.lockedRounds || []).some(Boolean);
}

/**
 * What a new running order means for the match on the judge's screen.
 *
 * A judge waiting on their next match, with nothing scored on it, follows
 * the order: they're put on the new next match. Anyone who has started
 * scoring stays where they are; only the number on screen may change.
 *
 * @param {Array} prev the order on screen
 * @param {Array} next the order just read
 * @param {Object<string, object>} cards the judge's cards by boutKey
 * @param {string|null} current the match on screen
 * @param {{busy?: boolean}} [opts] busy: the judge is mid-way through
 *   something on it (the end panel, the PIN sheet, a save on its way)
 * @returns {null|{follow: string|null, renumbered: {from: number, to: number}|null}}
 *   null when the order didn't change; follow: the match to show instead
 */
export function followOrder(prev, next, cards, current, { busy = false } = {}) {
    if (!isReordered(prev, next)) return null;
    const was = (prev || []).find((m) => m.boutKey === current);
    const now = (next || []).find((m) => m.boutKey === current);
    // The two ways the page puts a judge on their next match: NEXT MATCH
    // (the one after their furthest submitted card) and opening the page
    // (the first one not submitted). Whichever it was, the same rule on the
    // new order gives the match to follow to. Usually they're the same.
    const firstOpen = (list) => (list || []).find((m) => !cards?.[m.boutKey]?.submitted)?.boutKey ?? null;
    const after = (list) => nextToScore(list, cards);
    const rule = [after, firstOpen].find((f) => f(prev) === current);
    const waiting = !!was && !!now && !busy && !!rule && isUntouched(cards?.[current]);
    const target = waiting ? rule(next) : null;
    return {
        follow: target && target !== current ? target : null,
        renumbered: was && now && was.n !== now.n ? { from: was.n, to: now.n } : null,
    };
}
